import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import { subsetGetter, SubsetGetterProps, Subsets, subsetSetter } from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";

export type Avsandare = {
    id: number;
    namn: string;
    hsaid: string;
    is_active: boolean;
};

export type AvsandareDisplay = {
    value: Avsandare["id"];
    label: Avsandare["namn"];
};

type State = BaseState & {
    avsandare: Map<Avsandare["id"], Avsandare>;
};

export const convertAvsandareData: (args: { avsandare?: Avsandare }) => AvsandareDisplay | null = ({ avsandare }) => {
    if (!avsandare) {
        return null;
    }

    return {
        value: avsandare.id,
        label: avsandare.namn,
    };
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        avsandare: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "avsandare";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        avsandare: (state: State) => [...state.avsandare.values()],
        avsandareSubset: (state: State) => (props: SubsetGetterProps<State> & { displayMode?: boolean }) => {
            const result = subsetGetter<State>({
                ...props,
                state,
                limit: 0,
                collectionName: moduleName,
            });

            if (props.displayMode && Array.isArray(result)) {
                return result.map((singleResult) => convertAvsandareData({ avsandare: singleResult }));
            }

            return result;
        },
        avsandareById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }

                return state.avsandare.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addAvsandare: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                avsandare = [],
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                avsandare: Avsandare[];
            }
        ) =>
            subsetSetter<State, Avsandare>({
                offset,
                subsetName,
                state,
                resetSubset,
                collectionName: moduleName,
                items: avsandare,
            }),
    },
    actions: {
        ...baseActions({
            moduleName,
        }),
        fetchAvsandare: async (
            { commit, state }: BaseActionsProps<State>,
            {
                displayMode,
                filterParams = {},
                config = {},
            }: {
                offset?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
                displayMode?: boolean;
            }
        ) => {
            try {
                const {
                    subsetName,
                    results,
                    total,
                    nextPage,
                    offset: resultOffset,
                } = await fetchItems<State, Avsandare>({
                    filterParams,
                    state,
                    config,
                    page: 1,
                    limit: 0,
                    collectionName: moduleName,
                    apiUrl: "remiss/avsandare/",
                });

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addAvsandare", {
                    subsetName,
                    offset: resultOffset,
                    avsandare: results,
                });

                return {
                    total,
                    nextPage,
                    subsetName,
                    results: displayMode
                        ? results.map((singleResult) => convertAvsandareData({ avsandare: singleResult }))
                        : results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
